import createCachedSelector from 're-reselect';
import {RootState} from '../../rootReducer';
import {Area} from '../entities';
import {getBuildingEntities} from '../buildings/selectors';
import {getFloorEntities} from '../floors/selectors';
import {areasAdapter} from './adapters';
import {getWorkspaceOccupancyForArea} from '../sensors/selectors';
import {getAreaSensorInfo, getSensorLastUpdatedText} from '../sensors';
import {TranslationService} from '../../@types/i18next';
import {WorkspaceOccupancyState} from '@lib/infrastructure';

export const {
  selectById: getAreaById,
  selectIds: getAreaIds,
  selectEntities: getAreaEntities,
  selectAll: getAllAreas,
  selectTotal: getTotalAreas,
} = areasAdapter.getSelectors((state: RootState) => state.data.areas);

export const getAreasByFloorId = createCachedSelector(
  getFloorEntities,
  getAreaEntities,
  (_: RootState, floorId: string) => floorId,
  (floors, areas, floorId) => {
    const floor = floors[floorId];
    if (!floor) {
      return [];
    }

    return floor.areas.map((areaId) => areas[areaId] as Area);
  },
)((_: RootState, floorId: string) => floorId);

export const getAreasByBuildingId = createCachedSelector(
  getBuildingEntities,
  getFloorEntities,
  getAreaEntities,
  (_: RootState, buildingId: string) => buildingId,
  (buildings, floors, areas, buildingId) => {
    const building = buildings[buildingId];
    if (!building) {
      return [];
    }

    const areaIds = building.floors.flatMap((floorId) => {
      const floor = floors[floorId];
      if (floor) {
        return floor.areas;
      }
      return [];
    });

    return areaIds.map((areaId) => areas[areaId] as Area);
  },
)((_: RootState, buildingId: string) => buildingId);

export const getAreasWithSensorData = createCachedSelector(
  (state: RootState) => state,
  (state: RootState, buildingId: string) => buildingId,
  (state: RootState, buildingId: string, t: TranslationService) => t,
  getAreasByBuildingId,
  (state, buildingId, t, areas) => {
    const sensorDataByAreaId = areas.reduce((acc: {[key: string]: WorkspaceOccupancyState | null}, area) => {
      acc[area.id] = getWorkspaceOccupancyForArea(state, area.buildingId ?? '', area.id);
      return acc;
    }, {});

    const areasWithSensorData = areas.map((area) => ({
      ...area,
      sensorInformation: getAreaSensorInfo(t, area, sensorDataByAreaId[area.id]),
      sensorUpdateTimeLabel: getSensorLastUpdatedText(t, sensorDataByAreaId[area.id]?.stateTimestamp),
    }));
    return areasWithSensorData;
  },
)((_: RootState, buildingId: string, t: TranslationService) => buildingId);
